// Permission constants + labels shared between coach UI and access checks.

export const PERMISSION_LABELS = {
    // Pages & sections
    view_clients: { label: "View clients list", desc: "See lead/member roster basics + Find anyone search." },
    view_athletes: { label: "View athletes", desc: "See each family's athlete cards (name, age, track)." },
    view_schedule: { label: "Schedule", desc: "Schedule page + recurring scheduling from profiles." },
    view_attendance: { label: "Attendance & check-in", desc: "Attendance views, check-ins, today's attendance, logging sessions." },
    run_assessments: { label: "Assessments", desc: "Run, assign, and send assessments + recap emails." },
    view_messages: { label: "Messages", desc: "In-app messages with clients." },
    // Training authoring
    manage_workouts: { label: "Workouts", desc: "Create and edit workouts." },
    manage_programs: { label: "Programs", desc: "Create, edit, and assign training programs." },
    manage_exercises: { label: "Exercises", desc: "Create and edit the exercise library." },
    manage_equipment: { label: "Equipment", desc: "Manage the equipment list." },
    // Client profile details
    view_health_notes: { label: "View health notes", desc: "Read injury and medical notes." },
    view_emergency_contact: { label: "View emergency contact", desc: "See alternate contact details." },
    view_address_phone: { label: "View parent contact info", desc: "Phone and address of the parent/account holder." },
    view_waiver: { label: "View signed waivers", desc: "See waiver version, date, and signature." },
    // Client actions
    change_status: { label: "Change client status", desc: "Promote leads to member or mark non-member. Also required to save assessments." },
    view_billing: { label: "View client billing", desc: "Pack balances, credits, and card-on-file on profiles." },
    charge_cards: { label: "Charge cards", desc: "Charge a client's saved card for session packs." },
    email_clients: { label: "Email clients", desc: "Communication log + composing new emails to clients." },
    manage_strikes: { label: "Manage strikes", desc: "Forgive strikes, re-credit sessions, run no-show sweeps." },
    // Owner-only areas — grantable per coach (default off).
    manage_announcements: { label: "Announcements", desc: "Compose and send gym-wide announcements." },
    view_reports: { label: "Revenue & metrics", desc: "Revenue dashboard and weekly metrics." },
    manage_pricing: { label: "Pricing blocks", desc: "Create and edit the pricing blocks used for charging." },
};

export const PERMISSION_KEYS = Object.keys(PERMISSION_LABELS);

// Grouped layout for the per-coach checkbox editor ("Coaches" page).
export const PERMISSION_GROUPS = [
    { key: "pages", label: "Pages & sections", keys: ["view_clients", "view_athletes", "view_schedule", "view_attendance", "run_assessments", "view_messages"] },
    { key: "training", label: "Training authoring", keys: ["manage_workouts", "manage_programs", "manage_exercises", "manage_equipment"] },
    { key: "profile", label: "Client profile details", keys: ["view_health_notes", "view_emergency_contact", "view_address_phone", "view_waiver"] },
    { key: "actions", label: "Client actions", keys: ["change_status", "view_billing", "charge_cards", "email_clients", "manage_strikes"] },
    { key: "owner_areas", label: "Owner-only areas (grant with care)", keys: ["manage_announcements", "view_reports", "manage_pricing"] },
];

// Compact at-a-glance summary of what a coach can access, for the Coaches
// list. Owners get "Full access"; coaches get one chip per permission group
// they have (with x/total when partial), plus a named amber chip for each
// granted owner-only area.
const GROUP_SHORT = {
    pages: "Console",
    training: "Training",
    profile: "Client details",
    actions: "Client actions",
};
export function summarizeGrants(permissions, role) {
    if (role === "owner") return { full: true, groups: [], owner: [] };
    const perms = permissions || {};
    const groups = [];
    const owner = [];
    for (const g of PERMISSION_GROUPS) {
        const granted = g.keys.filter((k) => perms[k]);
        if (granted.length === 0) continue;
        if (g.key === "owner_areas") {
            granted.forEach((k) => owner.push(PERMISSION_LABELS[k].label));
        } else {
            groups.push({
                key: g.key,
                label: GROUP_SHORT[g.key] || g.label,
                partial: granted.length < g.keys.length,
                granted: granted.length,
                total: g.keys.length,
            });
        }
    }
    return { full: false, groups, owner };
}

// Mirrors backend DEFAULT_COACH_PERMISSIONS (deps.py).
export const PERMISSION_DEFAULTS = {
    view_clients: true,
    view_athletes: true,
    view_schedule: true,
    view_attendance: true,
    run_assessments: true,
    view_messages: true,
    manage_workouts: true,
    manage_programs: true,
    manage_exercises: true,
    manage_equipment: true,
    view_health_notes: false,
    view_emergency_contact: false,
    view_address_phone: false,
    view_waiver: false,
    change_status: true,
    view_billing: true,
    charge_cards: true,
    email_clients: true,
    manage_strikes: true,
    // Owner-only areas — off by default; owner grants per coach.
    manage_announcements: false,
    view_reports: false,
    manage_pricing: false,
};

export const STATUS_META = {
    lead: { label: "Lead", className: "border-zinc-700 text-zinc-300 bg-zinc-900/60" },
    // Prospect — replied to the initial email / scheduled to become a member
    // (Sachin 2026-08). Sits between Lead and Member; manual flip both ways.
    prospect: { label: "Prospect", className: "border-amber-500/70 text-amber-300 bg-amber-950/40" },
    // Assessed — came in and completed their assessment but hasn't signed up
    // yet. Sits between Prospect and Member; manual flip only (Sachin 2026-06).
    assessed: { label: "Assessed", className: "border-sky-500/70 text-sky-300 bg-sky-950/40" },
    assessment_active: { label: "Active", className: "border-brand-red/60 text-brand-red bg-brand-red/10" },
    assessment_inactive: { label: "Inactive", className: "border-zinc-700 text-zinc-500 bg-zinc-950" },
    member: { label: "Member", className: "border-brand-navy-700 text-brand-navy-200 bg-brand-navy-900/40" },
    non_member: { label: "Non-member", className: "border-zinc-800 text-zinc-500 bg-zinc-950" },
};

export const STATUS_ORDER = ["lead", "prospect", "assessed", "assessment_active", "assessment_inactive", "member", "non_member"];

export const TRACK_LABELS = {
    school_foundational: "Foundational Athleticism (8–10)",
    school_elite_youth: "Elite Youth Performance (11–14)",
    school_elite_peak: "Elite Peak Performance (14–18)",
    // Legacy values kept for any old records still in flight (defensive).
    youth_7_10: "Foundational Athleticism (8–10)",
    youth_10_13: "Elite Youth Performance (11–14)",
    youth_14_18: "Elite Peak Performance (14–18)",
    pursuing_pro: "Pursuing Pro",
    athletic_aesthetic: "Adult Performance",
};

export function calcAge(dobStr) {
    if (!dobStr) return null;
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
}
